import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { CircleHelp, X } from 'lucide-react';

/**
 * Ajuda de indicadores (reutilizável). `help` = { title, what, how, read }.
 * - `IndicatorHelp`: ícone "?" que abre um popover (clique/Enter/Espaço; toque no mobile).
 * - `IndicatorGlossaryButton`: botão que abre um diálogo com todos os indicadores.
 */

function HelpBody({ help }) {
    const sections = [
        ['O que mede', help.what],
        ['Como é calculado', help.how],
        ['Como interpretar', help.read],
    ];
    return (
        <>
            {sections.map(([label, text]) => (
                <Box key={label} sx={{ mt: 1.25 }}>
                    <Typography sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled' }}>{label}</Typography>
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5 }}>{text}</Typography>
                </Box>
            ))}
        </>
    );
}

export function IndicatorHelp({ help, size = 14 }) {
    const [anchor, setAnchor] = useState(null);
    if (!help) return null;
    const open = Boolean(anchor);
    return (
        <>
            <IconButton
                size="small"
                aria-label={`Ajuda: o que é ${help.title}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
                sx={{ p: '3px', color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
            >
                <CircleHelp size={size} strokeWidth={2} />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { maxWidth: 'min(360px, calc(100vw - 24px))', p: 2 } } }}
            >
                <Typography sx={{ fontSize: '14px', fontWeight: 800, color: 'text.primary' }}>{help.title}</Typography>
                <HelpBody help={help} />
            </Popover>
        </>
    );
}

export function IndicatorGlossaryButton({ items, title = 'Entenda os indicadores', fullScreen = false }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button
                size="small"
                variant="outlined"
                startIcon={<CircleHelp size={14} strokeWidth={2} />}
                onClick={() => setOpen(true)}
                aria-haspopup="dialog"
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '12px', whiteSpace: 'nowrap' }}
            >
                {title}
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} fullScreen={fullScreen} fullWidth maxWidth="sm" scroll="paper" aria-labelledby="indicator-glossary-title">
                <DialogTitle id="indicator-glossary-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '16px', fontWeight: 800 }}>
                    {title}
                    <IconButton aria-label="Fechar" onClick={() => setOpen(false)} size="small"><X size={18} /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {items.map((h, i) => (
                        <Box key={h.title} sx={{ pb: 1.5, mb: 1.5, ...(i < items.length - 1 && { borderBottom: '1px dashed', borderColor: 'divider' }) }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 800, color: 'text.primary' }}>{h.title}</Typography>
                            <HelpBody help={h} />
                        </Box>
                    ))}
                </DialogContent>
            </Dialog>
        </>
    );
}
